import { Bot, Globe, Plus, X } from 'lucide-react';
import { type ComponentType, type PointerEvent as ReactPointerEvent, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { SidebarHeader, SidebarItem, SidebarSection } from '@/components/sidebar/sidebar';
import { coreAppSidebarSlot } from '@/lib/desktop-shell/core-app-sidebar-slot';
import { splitPanelTab } from '@/lib/desktop-shell/panel-split';
import {
  type SidebarSearchSource,
  useSidebarSearchSource,
} from '@/lib/desktop-shell/sidebar-search';
import { useDesktopShellStoreApi } from '@/lib/desktop-shell/store-context';
import { type BrowserSession, browserSessionDisplayName } from '@/lib/side-panel/browser-session';
import { dragIntoPanelSplit } from '@/lib/side-panel/split-drag';

const faviconIcons = new Map<string, ComponentType<{ className?: string }>>();

/** The page's favicon as a sidebar icon, the same component for the same image. */
function faviconIcon(url: string): ComponentType<{ className?: string }> {
  let icon = faviconIcons.get(url);
  if (icon === undefined) {
    icon = ({ className }) => (
      <img
        alt=""
        className={`${className ?? ''} rounded-sm object-contain`}
        draggable={false}
        referrerPolicy="no-referrer"
        src={url}
      />
    );
    faviconIcons.set(url, icon);
  }
  return icon;
}

type TabsProps = {
  sessions: readonly BrowserSession[];
  activeId: string;
  controlledIds: ReadonlySet<string>;
  onSelect: (id: string) => void;
  onClose?: ((id: string) => void) | undefined;
  onNew?: (() => void) | undefined;
};

/**
 * The browser's tabs, listed down the shell sidebar on the browser's side of its toggle (as in Arc):
 * a new tab first, then each tab with its page's icon and title, the one shown highlighted.
 */
export function BrowserSidebarTabs(props: TabsProps) {
  const slot = coreAppSidebarSlot.useElement();
  return slot === null ? null : createPortal(<TabList {...props} />, slot);
}

/** Mounted only while listed, so that the sidebar's search finds tabs only then. */
function TabList({ sessions, activeId, controlledIds, onSelect, onClose, onNew }: TabsProps) {
  const store = useDesktopShellStoreApi();
  const search = useMemo<SidebarSearchSource>(
    () => ({
      title: 'Search tabs',
      placeholder: 'Search tabs…',
      empty: 'No tab found.',
      groups: [
        {
          heading: 'Tabs',
          items: sessions.map((session) => ({
            id: session.id,
            label: browserSessionDisplayName(session),
            current: session.id === activeId,
          })),
        },
      ],
      onSelect: (item) => onSelect(item.id),
    }),
    [sessions, activeId, onSelect],
  );
  useSidebarSearchSource(search);
  return (
    <nav aria-label="Browser tabs">
      <SidebarHeader title="Tabs" />
      {onNew !== undefined && (
        <SidebarSection>
          <SidebarItem icon={Plus} label="New tab" onSelect={onNew} />
        </SidebarSection>
      )}
      <SidebarSection>
        {sessions.map((session) => {
          const name = browserSessionDisplayName(session);
          return (
            <div className="group relative" key={session.id}>
              <SidebarItem
                active={session.id === activeId}
                className={onClose === undefined ? undefined : 'pr-8'}
                icon={session.favicon === undefined ? Globe : faviconIcon(session.favicon)}
                label={name}
                onSelect={() => onSelect(session.id)}
                // Dragged over the panel, the tab shows beside the one it is dropped on.
                onPointerDown={(event: ReactPointerEvent<HTMLElement>) =>
                  dragIntoPanelSplit(event, (preview) =>
                    splitPanelTab(store, session.id, preview.targetId, preview.side),
                  )
                }
                title={name}
                trailing={
                  controlledIds.has(session.id) ? (
                    <Bot
                      aria-label="An agent is using this tab"
                      className="size-3.5 text-muted-foreground"
                    />
                  ) : undefined
                }
              />
              {onClose !== undefined && (
                <button
                  aria-label={`Close ${name} tab`}
                  className="absolute top-1 right-1 flex size-6 items-center justify-center rounded-md text-muted-foreground can-hover:opacity-0 transition-opacity hover:bg-background/70 hover:text-foreground focus:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring group-hover:opacity-100"
                  onClick={() => onClose(session.id)}
                  type="button"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </SidebarSection>
    </nav>
  );
}
